import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  TextField, Button,
} from '@mui/material';
import SelectField from '../inputs/SelectField';

/**
 * Minimal always-visible filter bar for inventory pages.
 *
 * Props:
 *   shapes              string[]  — dropdown options
 *   weightLabel         string    — field label ("Carat" / "Ct/stone")
 *   weightTolerance     number    — ±range applied to the entered weight (default 0.20)
 *   lengthTolerance     number    — ±range applied to the entered length (default 0.50)
 *   shapeValue          string    — controlled shape (synced into internal state when changed externally)
 *   weightCenterValue   string    — controlled weight center
 *   lengthCenterValue   string    — controlled length center
 *   onApply  fn({ shape, weightCenter, minWeight, maxWeight, lengthCenter, minLength, maxLength }) => void
 *   onClear  fn() => void
 */
const QuickFilterBar = React.memo(function QuickFilterBar({
  shapes = [],
  weightLabel = 'Carat',
  weightTolerance = 0.20,
  lengthTolerance = 0.50,
  shapeValue = '',
  weightCenterValue = '',
  lengthCenterValue = '',
  includeAllShapeOption = true,
  defaultShape = '',
  onApply,
  onClear,
}) {
  const [shape, setShape] = useState(shapeValue);
  const [weightCenter, setWeightCenter] = useState(weightCenterValue);
  const [lengthCenter, setLengthCenter] = useState(lengthCenterValue);

  // Sync internal state when the parent clears or changes a field externally
  // (e.g. chip removal). React bails out when the value hasn't changed.
  useEffect(() => { setShape(shapeValue); }, [shapeValue]);
  useEffect(() => { setWeightCenter(weightCenterValue); }, [weightCenterValue]);
  useEffect(() => { setLengthCenter(lengthCenterValue); }, [lengthCenterValue]);

  const buildPayload = useCallback((overrides = {}) => {
    const s = overrides.shape !== undefined ? overrides.shape : shape;
    const wc = overrides.weightCenter !== undefined ? overrides.weightCenter : weightCenter;
    const lc = overrides.lengthCenter !== undefined ? overrides.lengthCenter : lengthCenter;

    const wNum = parseFloat(wc);
    const lNum = parseFloat(lc);

    return {
      shape: s,
      weightCenter: wc,
      minWeight: wc !== '' && !isNaN(wNum) ? String(Math.max(0, Math.round((wNum - weightTolerance) * 1000) / 1000)) : '',
      maxWeight: wc !== '' && !isNaN(wNum) ? String(Math.round((wNum + weightTolerance) * 1000) / 1000) : '',
      lengthCenter: lc,
      minLength: lc !== '' && !isNaN(lNum) ? String(Math.max(0, Math.round((lNum - lengthTolerance) * 1000) / 1000)) : '',
      maxLength: lc !== '' && !isNaN(lNum) ? String(Math.round((lNum + lengthTolerance) * 1000) / 1000) : '',
    };
  }, [shape, weightCenter, lengthCenter, weightTolerance, lengthTolerance]);

  const handleShapeChange = (newShape) => {
    setShape(newShape);
    onApply?.(buildPayload({ shape: newShape }));
  };

  const commitWeight = () => { onApply?.(buildPayload()); };
  const commitLength = () => { onApply?.(buildPayload()); };

  const handleKeyDown = (commitFn) => (e) => {
    if (e.key === 'Enter') { e.preventDefault(); commitFn(); }
  };

  const handleClear = () => {
    const resetShape = includeAllShapeOption ? '' : (defaultShape || shape);
    setShape(resetShape);
    setWeightCenter('');
    setLengthCenter('');
    if (includeAllShapeOption) {
      onClear?.();
    } else {
      onApply?.(buildPayload({ shape: resetShape, weightCenter: '', lengthCenter: '' }));
    }
  };

  const hasValue = (includeAllShapeOption ? shape : (shape && shape !== defaultShape))
    || weightCenter || lengthCenter;

  return (
    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, alignItems: 'center', flex: 1, minWidth: 0 }}>
      <SelectField
        label="Shape"
        value={shape}
        onChange={(next) => handleShapeChange(next)}
        options={shapes.map((s) => {
          const val = String(s).toUpperCase();
          return { value: val, label: val };
        })}
        emptyLabel="All"
        includeEmptyOption={includeAllShapeOption}
        size="small"
        sx={{ minWidth: 120 }}
      />

      <TextField
        size="small"
        label={`${weightLabel} (±${weightTolerance})`}
        type="number"
        value={weightCenter}
        onChange={(e) => setWeightCenter(e.target.value)}
        onBlur={commitWeight}
        onKeyDown={handleKeyDown(commitWeight)}
        inputProps={{ step: 0.01, min: 0 }}
        sx={{ width: 135 }}
      />

      <TextField
        size="small"
        label={`Length mm (±${lengthTolerance})`}
        type="number"
        value={lengthCenter}
        onChange={(e) => setLengthCenter(e.target.value)}
        onBlur={commitLength}
        onKeyDown={handleKeyDown(commitLength)}
        inputProps={{ step: 0.1, min: 0 }}
        sx={{ width: 150 }}
      />

      {hasValue && (
        <Button size="small" variant="text" color="inherit" onClick={handleClear} sx={{ flexShrink: 0 }}>
          Clear
        </Button>
      )}
    </Box>
  );
});

export default QuickFilterBar;
